import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, Trash2, Info, XCircle, LucideIcon, Loader2 } from "lucide-react";

type DialogVariant = "default" | "destructive" | "warning" | "info" | "error";

interface ConfirmOptions {
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    variant?: DialogVariant;
    icon?: LucideIcon;
    onConfirm?: () => Promise<void> | void;
}

interface AlertOptions {
    title: string;
    description: string;
    variant?: DialogVariant;
    icon?: LucideIcon;
}

interface ConfirmContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
    alert: (options: AlertOptions) => Promise<void>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error("useConfirm must be used within ConfirmProvider");
    }
    return context;
}

interface ConfirmProviderProps {
    children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAlertMode, setIsAlertMode] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

    const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setOptions({
                confirmText: "دڵنیام",
                cancelText: "پاشگەزبوونەوە",
                variant: "default",
                ...opts,
            });
            setIsAlertMode(false);
            setResolveRef(() => resolve);
            setIsOpen(true);
        });
    }, []);

    const alert = useCallback((opts: AlertOptions): Promise<void> => {
        return new Promise((resolve) => {
            setOptions({
                confirmText: "باشە",
                cancelText: "",
                variant: opts.variant || "info",
                ...opts,
            });
            setIsAlertMode(true);
            setResolveRef(() => () => resolve());
            setIsOpen(true);
        });
    }, []);

    const handleConfirm = useCallback(async () => {
        let success = true;
        if (options?.onConfirm) {
            setIsLoading(true);
            try {
                await options.onConfirm();
            } catch (error) {
                console.error('Error in onConfirm:', error);
                success = false;
            } finally {
                setIsLoading(false);
                setIsOpen(false);
                resolveRef?.(success);
            }
        } else {
            setIsOpen(false);
            resolveRef?.(true);
        }
    }, [resolveRef, options]);

    const handleCancel = useCallback(() => {
        if (isLoading) return;
        setIsOpen(false);
        resolveRef?.(false);
    }, [resolveRef, isLoading]);

    const getVariantStyles = (variant: DialogVariant) => {
        switch (variant) {
            case "destructive":
                return {
                    iconBg: "bg-red-100 dark:bg-red-900/30",
                    iconColor: "text-red-600 dark:text-red-400",
                    buttonClass: "bg-red-600 text-white hover:bg-red-700",
                    DefaultIcon: Trash2,
                };
            case "warning":
                return {
                    iconBg: "bg-amber-100 dark:bg-amber-900/30",
                    iconColor: "text-amber-600 dark:text-amber-400",
                    buttonClass: "bg-amber-600 text-white hover:bg-amber-700",
                    DefaultIcon: AlertTriangle,
                };
            case "error":
                return {
                    iconBg: "bg-red-100 dark:bg-red-900/30",
                    iconColor: "text-red-600 dark:text-red-400",
                    buttonClass: "bg-red-600 text-white hover:bg-red-700",
                    DefaultIcon: XCircle,
                };
            case "info":
                return {
                    iconBg: "bg-blue-100 dark:bg-blue-900/30",
                    iconColor: "text-blue-600 dark:text-blue-400",
                    buttonClass: "bg-blue-600 text-white hover:bg-blue-700",
                    DefaultIcon: Info,
                };
            default:
                return {
                    iconBg: "bg-primary/10",
                    iconColor: "text-primary",
                    buttonClass: "",
                    DefaultIcon: CheckCircle,
                };
        }
    };

    const styles = options ? getVariantStyles(options.variant || "default") : getVariantStyles("default");
    const DisplayIcon = options?.icon || styles.DefaultIcon;

    return (
        <ConfirmContext.Provider value={{ confirm, alert }}>
            {children}
            <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && handleCancel()}>
                <DialogContent className="sm:max-w-md max-w-[calc(100%-2rem)]" showCloseButton={false} onInteractOutside={(e) => isLoading && e.preventDefault()}>
                    {options && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`h-12 w-12 rounded-full ${styles.iconBg} flex items-center justify-center flex-shrink-0`}>
                                        <DisplayIcon className={`h-6 w-6 ${styles.iconColor}`} />
                                    </div>
                                    <div>
                                        <DialogTitle className="text-right text-lg">
                                            {options.title}
                                        </DialogTitle>
                                        <DialogDescription className="text-right text-sm mt-1">
                                            {options.description}
                                        </DialogDescription>
                                    </div>
                                </div>
                            </DialogHeader>

                            <div className="flex gap-3 pt-4">
                                {!isAlertMode && options.cancelText && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        className="flex-1"
                                        disabled={isLoading}
                                    >
                                        {options.cancelText}
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    onClick={handleConfirm}
                                    className={`flex-1 ${styles.buttonClass}`}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin ml-2" />
                                            چاوەڕوان بە...
                                        </>
                                    ) : (
                                        options.confirmText
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </ConfirmContext.Provider>
    );
}
